import { z } from "zod"

import { revisionSchema } from "./sync.ts"
import { tripHoldReasonSchema, tripVisibilitySchema } from "./trips.ts"

/**
 * 1. Odometer reading kind (s4-trips-api-brief §1.6).
 */
export const odometerReadingKindSchema = z.enum([
  "fy_start",
  "fy_end",
  "logbook_start",
  "logbook_end",
  "other",
])
export type OdometerReadingKind = z.infer<typeof odometerReadingKindSchema>

/**
 * 2. Odometer reading origin (s4-trips-api-brief §1.7).
 */
export const odometerReadingOriginSchema = z.enum(["gps_derived", "manual"])
export type OdometerReadingOrigin = z.infer<typeof odometerReadingOriginSchema>

/** `read_at` on a request may be at most this far ahead of the server clock (clock skew). */
export const READ_AT_FUTURE_TOLERANCE_MS = 5 * 60_000
/** `read_at` on a request may not be earlier than this (2000-01-01T00:00:00Z). */
export const READ_AT_MIN_MS = Date.UTC(2000, 0, 1)

/**
 * Request-side `read_at` (strict): ISO 8601 with offset, not before 2000, not in the future
 * beyond a small tolerance; `null` clears. The stored/response shape stays lenient.
 */
export const readAtRequestSchema = z
  .string()
  .datetime({ offset: true })
  .refine((value) => Date.parse(value) >= READ_AT_MIN_MS, "read_at must not be before 2000")
  .refine(
    (value) => Date.parse(value) <= Date.now() + READ_AT_FUTURE_TOLERANCE_MS,
    "read_at must not be in the future",
  )
  .nullable()

/**
 * Odometer reading entity schema as stored and synchronized (PRD R4.11).
 */
export const odometerReadingSchema = z.object({
  id: z.uuid(),
  organisation_id: z.uuid(),
  vehicle_id: z.uuid(),
  trip_id: z.uuid().nullable(),
  read_on: z.string().date(),
  /**
   * The instant the car's odometer was read (T119). Lenient on purpose: null for legacy
   * rows. When present it must share the local date of `read_on` to be used as the anchor
   * (docs/specs/vehicle-odometer-policy.md section 2); otherwise clients ignore it.
   */
  read_at: z.string().datetime({ offset: true }).nullable().default(null),
  value_km: z.number().int(),
  kind: odometerReadingKindSchema,
  origin: odometerReadingOriginSchema,
  original_value_km: z.number().int().nullable(),
  edited_at: z.string().datetime({ offset: true }).nullable(),
  visibility: tripVisibilitySchema.default("full"),
  hold_reason: tripHoldReasonSchema.nullable().default(null),
  revision: revisionSchema,
  deleted_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
})

export type OdometerReading = z.infer<typeof odometerReadingSchema>

/**
 * Request payload to create an odometer reading.
 */
export const createOdometerReadingRequestSchema = z.object({
  id: z.uuid().optional(),
  vehicle_id: z.uuid(),
  trip_id: z.uuid().nullable().optional().default(null),
  read_on: z.string().date(),
  read_at: readAtRequestSchema.optional().default(null),
  value_km: z.number().int().nonnegative(),
  kind: odometerReadingKindSchema.default("other"),
  origin: odometerReadingOriginSchema.default("manual"),
  original_value_km: z.number().int().nonnegative().nullable().optional().default(null),
  edited_at: z.string().datetime({ offset: true }).nullable().optional().default(null),
})

export type CreateOdometerReadingRequest = z.input<typeof createOdometerReadingRequestSchema>
export type ParsedCreateOdometerReadingRequest = z.infer<typeof createOdometerReadingRequestSchema>

/**
 * Request payload to update an odometer reading.
 */
export const updateOdometerReadingRequestSchema = z.object({
  base_revision: z.number().int().nonnegative().optional(),
  read_on: z.string().date().optional(),
  read_at: readAtRequestSchema.optional(),
  value_km: z.number().int().nonnegative().optional(),
  kind: odometerReadingKindSchema.optional(),
  origin: odometerReadingOriginSchema.optional(),
  original_value_km: z.number().int().nonnegative().nullable().optional(),
  edited_at: z.string().datetime({ offset: true }).nullable().optional(),
})

export type UpdateOdometerReadingRequest = z.infer<typeof updateOdometerReadingRequestSchema>
